import { Field } from '@catering/ui';
import type { JSX } from 'react';
import { useEffect } from 'react';
import {
  useFieldArray,
  type ArrayPath,
  type Control,
  type FieldArray,
  type FieldErrors,
  type FieldPath,
  type FieldValues,
  type UseFormRegister,
} from 'react-hook-form';

import {
  defaultMenuLine,
  syncLinesToPositions,
  type MenuLineFormValues,
} from '../../../orders/menu-line-form-schema';

export interface OrderItemEditorRow {
  positionId: string;
  positionName: string;
  serviceTypeId: string;
  serviceTypeName: string;
  displayOrder: number;
  /** The dish published in this position for the selected `serviceDate`. */
  dishName: string;
}

export interface OrderItemEditorProps<
  TFieldValues extends FieldValues & { items: MenuLineFormValues[] },
> {
  control: Control<TFieldValues>;
  register: UseFormRegister<TFieldValues>;
  /**
   * Positions with a published dish that day, grouped by service type and
   * ordered by `displayOrder` (`rotating-menu/design.md` §4). A position
   * without a published dish never appears as a row. `undefined` while the
   * calendar for the day is still loading — kept distinct from an empty
   * array ("no position has a published dish") so the sync effect below
   * never wipes a pre-filled row before the day's calendar has resolved.
   */
  rows: OrderItemEditorRow[] | undefined;
  errors: FieldErrors<TFieldValues>['items'];
}

type OrderLineFieldError = {
  quantity?: { message?: string };
  notes?: { message?: string };
};

/**
 * `rotating-menu/design.md` §4: one fixed row per position with a
 * published dish that day, grouped by service type — no "add line"/
 * "remove line", a position without a dish that day simply never
 * appears. Replaces the previous free menu-select field array
 * (`tasks.md` PR 10).
 */
export function OrderItemEditor<TFieldValues extends FieldValues & { items: MenuLineFormValues[] }>({
  control,
  register,
  rows,
  errors,
}: OrderItemEditorProps<TFieldValues>): JSX.Element {
  const { fields, replace } = useFieldArray({
    control,
    name: 'items' as ArrayPath<TFieldValues>,
  });
  const positionIdsKey = rows === undefined ? undefined : rows.map((row) => row.positionId).join(',');

  useEffect(() => {
    if (positionIdsKey === undefined) {
      return;
    }

    const positionIds = positionIdsKey === '' ? [] : positionIdsKey.split(',');
    const current = fields as unknown as MenuLineFormValues[];
    const next = syncLinesToPositions(current, positionIds, defaultMenuLine);

    if (
      next.length !== current.length ||
      next.some((item, index) => item.positionId !== current[index]?.positionId)
    ) {
      replace(next as unknown as FieldArray<TFieldValues, ArrayPath<TFieldValues>>[]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `fields`/`replace` intentionally excluded: this effect only re-syncs when the set of available positions changes, never on every keystroke.
  }, [positionIdsKey]);

  if (rows === undefined) {
    return <p className="text-sm text-slate-500">Buscando los menús publicados para esa fecha…</p>;
  }

  const groups = groupByServiceType(rows);

  if (rows.length === 0) {
    return (
      <p className="text-sm text-slate-500">
        No hay posiciones con un plato publicado para esa fecha.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {groups.map((group) => (
        <div key={group.serviceTypeId} className="flex flex-col gap-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {group.serviceTypeName}
          </h3>
          {group.rows.map((row) => {
            const index = rows.findIndex((candidate) => candidate.positionId === row.positionId);
            const field = fields[index];

            if (field === undefined) {
              return null;
            }

            const lineError: OrderLineFieldError | undefined = Array.isArray(errors)
              ? (errors as OrderLineFieldError[])[index]
              : undefined;

            return (
              <div
                key={field.id}
                data-testid={`order-item-row-${row.positionId}`}
                className="grid grid-cols-1 gap-3 sm:grid-cols-[1.5fr_1.5fr_1fr_2fr] sm:items-end"
              >
                <div className="flex flex-col justify-end pb-2.5">
                  <span className="text-sm font-medium text-slate-900">{row.positionName}</span>
                </div>

                <div className="flex flex-col justify-end pb-2.5">
                  <span className="text-sm text-slate-700">{row.dishName}</span>
                </div>

                <Field
                  label="Cantidad"
                  type="number"
                  min={0}
                  defaultValue={(field as unknown as MenuLineFormValues).quantity}
                  {...register(`items.${index}.quantity` as FieldPath<TFieldValues>)}
                  {...(lineError?.quantity?.message === undefined
                    ? {}
                    : { error: lineError.quantity.message })}
                />

                <Field
                  label="Notas"
                  defaultValue={(field as unknown as MenuLineFormValues).notes}
                  {...register(`items.${index}.notes` as FieldPath<TFieldValues>)}
                  {...(lineError?.notes?.message === undefined
                    ? {}
                    : { error: lineError.notes.message })}
                />
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

interface ServiceTypeGroup {
  serviceTypeId: string;
  serviceTypeName: string;
  rows: OrderItemEditorRow[];
}

function groupByServiceType(rows: OrderItemEditorRow[]): ServiceTypeGroup[] {
  const groups = new Map<string, ServiceTypeGroup>();

  for (const row of [...rows].sort((a, b) => a.displayOrder - b.displayOrder)) {
    const existing = groups.get(row.serviceTypeId);

    if (existing === undefined) {
      groups.set(row.serviceTypeId, {
        serviceTypeId: row.serviceTypeId,
        serviceTypeName: row.serviceTypeName,
        rows: [row],
      });
    } else {
      existing.rows.push(row);
    }
  }

  return [...groups.values()];
}
